import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, keyframes } from 'styled-components';
import Button from '../atom/Button';
import strings from '../../constants/strings.constants';
import Typo from '../atom/Typo';
import useAlertStore from '../../store/useAlertStore';

function AlertPortal({ children }: { children?: React.ReactNode }) {
  return createPortal(children, document.body);
}

export interface AlertProps {
  onConfirm?: () => void;
  title: string;
  showCancelButton?: boolean;
}
function Alert(props: AlertProps) {
  const { onConfirm, title, showCancelButton = false } = props;
  const { hideAlert } = useAlertStore();
  const [visible, setVisible] = useState(true);

  const handleClose = () => {
    setVisible(false);
    if (onConfirm) {
      onConfirm();
      hideAlert();
    } else {
      setTimeout(() => {
        hideAlert();
      }, 300);
    }
  };

  return (
    <AlertPortal>
      <AlertDialogOverlay $visible={visible}>
        <AlertDialogArticle $visible={visible}>
          <div className="flex flex-col gap-4">
            <div className="text-center">
              <div
                style={{
                  width: '100%',
                }}
              >
                <Typo
                  style={{
                    display: 'inline-block',
                  }}
                  type="r-16"
                >
                  {title}
                </Typo>
              </div>
            </div>
            <div className="flex gap-2 mt-1">
              {showCancelButton && (
                <Button
                  size="small"
                  type="gray"
                  style={{
                    width: '100%',
                  }}
                  onClick={() => {
                    setVisible(false);
                    setTimeout(() => {
                      hideAlert();
                    }, 300);
                  }}
                >
                  {strings.cancel}
                </Button>
              )}
              <Button
                size="small"
                style={{
                  width: '100%',
                }}
                onClick={() => {
                  handleClose();
                }}
              >
                {strings.confirm}
              </Button>
            </div>
          </div>
        </AlertDialogArticle>
      </AlertDialogOverlay>
    </AlertPortal>
  );
}

export default Alert;

const overlayShow = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const overlayHide = keyframes`
  to {
    opacity: 0;
  }
  from {
    opacity: 1;
  }
`;

const contentShow = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -30%) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

const contentHide = keyframes`
  to {
    opacity: 0;
    transform: translate(-50%, -30%) scale(0.9);
  }
  from {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

const AlertDialogOverlay = styled.div<{
  zIndexCustom?: number;
  $visible: boolean;
}>`
  position: fixed;
  z-index: 100;
  background: rgba(0, 0, 0, 0.6);
  inset: 0;
  ${(props) => {
    if (props.$visible) {
      return css`
        animation: ${overlayShow} 300ms cubic-bezier(0.16, 1, 0.3, 1);
      `;
    } else {
      return css`
        animation: ${overlayHide} 300ms cubic-bezier(0.16, 1, 0.3, 1);
      `;
    }
  }};
`;

const AlertDialogArticle = styled.article<{ size?: number; $visible: boolean }>`
  position: fixed;
  width: calc(100% - 48px);
  max-width: 340px;
  padding: 24px;
  background: white;
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.04),
    0 24px 72px rgba(36, 42, 48, 0.22);
  border-radius: 16px;
  top: 45%;
  left: 50%;
  transform: translate(-50%, -50%);
  ${(props) => {
    if (props.$visible) {
      return css`
        animation: ${contentShow} 300ms cubic-bezier(0.16, 1, 0.3, 1);
      `;
    } else {
      return css`
        animation: ${contentHide} 300ms cubic-bezier(0.16, 1, 0.3, 1);
      `;
    }
  }};
`;
